import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionHeader from "../SectionHeader";
import { Smartphone, Plus, Radio, Search, Bluetooth, Edit3, Check, CreditCard, Key, Scan, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/InteractiveSetupProcess-fa.json";
import en from "./locales/InteractiveSetupProcess-en.json";
import ar from "./locales/InteractiveSetupProcess-ar.json";
import tr from "./locales/InteractiveSetupProcess-tr.json";

interface Step {
    title: string;
    description: string;
    icon: React.ReactNode;
    details: string[];
    color: string;
    quickGuide: string[];
}

interface ProcessCategory {
    id: string;
    title: string;
    subtitle: string;
    steps: Step[];
}

const InteractiveSetupProcess: React.FC = () => {
    const { t, i18n } = useTranslation();

    // Load translation resources
    useEffect(() => {
        const ns = "InteractiveSetupProcess";
        const resources = { fa, en, ar, tr } as const;
        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";

    const colorClasses = {
        orange: {
            text: "text-blue-400",
            textDark: "text-blue-600",
            bg: "bg-blue-100",
            bgDark: "bg-blue-900/30",
            border: "border-blue-200",
            borderDark: "border-blue-700",
            gradientFrom: "from-blue-400",
            gradientTo: "to-blue-600",
            ring: "ring-blue-400",
        },
        deepOrange: {
            text: "text-cyan-500",
            textDark: "text-cyan-700",
            bg: "bg-cyan-200",
            bgDark: "bg-cyan-800/30",
            border: "border-cyan-300",
            borderDark: "border-cyan-600",
            gradientFrom: "from-cyan-500",
            gradientTo: "to-cyan-700",
            ring: "ring-cyan-500",
        },
        amber: {
            text: "text-sky-400",
            textDark: "text-sky-600",
            bg: "bg-sky-100",
            bgDark: "bg-sky-900/30",
            border: "border-sky-200",
            borderDark: "border-sky-700",
            gradientFrom: "from-sky-400",
            gradientTo: "to-sky-600",
            ring: "ring-sky-400",
        },
        red: {
            text: "text-red-400",
            textDark: "text-red-600",
            bg: "bg-red-100",
            bgDark: "bg-red-900/30",
            border: "border-red-200",
            borderDark: "border-red-700",
            gradientFrom: "from-red-400",
            gradientTo: "to-red-600",
            ring: "ring-red-400",
        },
        yellow: {
            text: "text-yellow-400",
            textDark: "text-yellow-600",
            bg: "bg-yellow-100",
            bgDark: "bg-yellow-900/30",
            border: "border-yellow-200",
            borderDark: "border-yellow-700",
            gradientFrom: "from-yellow-400",
            gradientTo: "to-yellow-600",
            ring: "ring-yellow-400",
        },
        green: {
            text: "text-green-400",
            textDark: "text-green-600",
            bg: "bg-green-100",
            bgDark: "bg-green-900/30",
            border: "border-green-200",
            borderDark: "border-green-700",
            gradientFrom: "from-green-400",
            gradientTo: "to-green-600",
            ring: "ring-green-400",
        },
    };

    const [activeStep, setActiveStep] = useState<number>(0);
    const [hoveredStep, setHoveredStep] = useState<number | null>(null);
    const [activeCategory, setActiveCategory] = useState<string>("add-device");
    const [showCompletionAnimation, setShowCompletionAnimation] = useState<boolean>(false);

    // Icons for steps
    const stepIcons = {
        addDevice: [
            <Smartphone className="w-6 h-6" key="ad0" />,
            <Plus className="w-6 h-6" key="ad1" />,
            <Search className="w-6 h-6" key="ad2" />,
            <Bluetooth className="w-6 h-6" key="ad3" />,
            <Edit3 className="w-6 h-6" key="ad4" />,
            <Check className="w-6 h-6" key="ad5" />,
        ],
        addMonitoring: [
            <Smartphone className="w-6 h-6" key="am0" />,
            <CreditCard className="w-6 h-6" key="am1" />,
            <Key className="w-6 h-6" key="am2" />,
            <Scan className="w-6 h-6" key="am3" />,
            <CheckCircle className="w-6 h-6" key="am4" />,
        ],
    };

    const stepColors = {
        addDevice: ["orange", "deepOrange", "amber", "red", "yellow", "green"],
        addMonitoring: ["orange", "yellow", "orange", "red", "green"],
    };

    // Build process categories dynamically from translations
    const processCategories: ProcessCategory[] = [
        {
            id: "add-device",
            title: t("categories.addDevice.title", { ns: "InteractiveSetupProcess", defaultValue: "" }),
            subtitle: t("categories.addDevice.subtitle", { ns: "InteractiveSetupProcess", defaultValue: "" }),
            steps: Array.from({ length: 6 }, (_, i) => {
                const step = t(`steps.addDevice.${i}`, { ns: "InteractiveSetupProcess", returnObjects: true, defaultValue: {} }) as any;
                return {
                    title: step?.title || "",
                    description: step?.description || "",
                    details: step?.details || [],
                    quickGuide: step?.quickGuide || [],
                    icon: stepIcons.addDevice[i],
                    color: stepColors.addDevice[i],
                };
            }),
        },
        {
            id: "add-monitoring-profile",
            title: t("categories.addMonitoring.title", { ns: "InteractiveSetupProcess", defaultValue: "" }),
            subtitle: t("categories.addMonitoring.subtitle", { ns: "InteractiveSetupProcess", defaultValue: "" }),
            steps: Array.from({ length: 5 }, (_, i) => {
                const step = t(`steps.addMonitoring.${i}`, { ns: "InteractiveSetupProcess", returnObjects: true, defaultValue: {} }) as any;
                return {
                    title: step?.title || "",
                    description: step?.description || "",
                    details: step?.details || [],
                    quickGuide: step?.quickGuide || [],
                    icon: stepIcons.addMonitoring[i],
                    color: stepColors.addMonitoring[i],
                };
            }),
        },
    ];

    const currentCategory = processCategories.find((cat) => cat.id === activeCategory) || processCategories[0];
    const steps = currentCategory.steps;

    const handleStepClick = (index: number) => {
        setActiveStep(index);
    };

    const handleCategoryChange = (categoryId: string) => {
        setActiveCategory(categoryId);
        setActiveStep(0);
        if (showCompletionAnimation) {
            setShowCompletionAnimation(false);
        }
    };

    const handleCompletion = () => {
        setShowCompletionAnimation(true);
        setTimeout(() => {
            setShowCompletionAnimation(false);
        }, 5000);
    };

    const currentStep = steps[activeStep];
    const colors = colorClasses[currentStep?.color as keyof typeof colorClasses] || colorClasses.orange;

    return (
        <div className="overflow-hidden px-4 sm:px-10" dir={dir}>
            <div className="absolute inset-0 pointer-events-none z-0" />

            {/* Completion Animation */}
            <AnimatePresence>
                {showCompletionAnimation && (
                    <motion.div className="fixed inset-0 z-50 flex items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                        <div className="absolute inset-0 bg-white/30 backdrop-blur-md"></div>

                        <motion.div
                            className="relative z-10 bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-auto text-center"
                            initial={{ scale: 0.8, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.2 }}
                        >
                            <motion.div
                                className="mx-auto mb-6 bg-green-100 w-24 h-24 rounded-full flex items-center justify-center"
                                initial={{ rotate: 0 }}
                                animate={{ rotate: [0, 15, -15, 10, -10, 5, -5, 0] }}
                                transition={{ duration: 1, delay: 0.3 }}
                            >
                                <motion.svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-12 w-12 text-green-500"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    initial={{ pathLength: 0, opacity: 0 }}
                                    animate={{ pathLength: 1, opacity: 1 }}
                                    transition={{ duration: 0.8, delay: 0.5 }}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </motion.svg>
                            </motion.div>

                            <motion.h2 className="text-2xl font-bold text-green-600 mb-3 text-center" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
                                {t("completion.congratulations", { ns: "InteractiveSetupProcess", defaultValue: "🎉 تبریک!" })}
                            </motion.h2>

                            <motion.p className="text-xl font-semibold text-green-500 mb-6" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
                                {t("completion.ready", { ns: "InteractiveSetupProcess", defaultValue: "سرویس آماده است" })}
                            </motion.p>

                            <div className="absolute -top-4 -right-4">
                                <motion.div className="text-4xl" initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.1 }}>
                                    🎊
                                </motion.div>
                            </div>

                            <div className="absolute -bottom-4 -left-4">
                                <motion.div className="text-4xl" initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.2 }}>
                                    🎉
                                </motion.div>
                            </div>

                            <motion.button
                                className="mt-4 px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                                onClick={() => setShowCompletionAnimation(false)}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 1.3 }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {t("completion.continue", { ns: "InteractiveSetupProcess", defaultValue: "ادامه" })}
                            </motion.button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Section Header */}
            <div className="text-center mb-10">
                <SectionHeader
                    regularText={t("sectionHeader.regularText", { ns: "InteractiveSetupProcess", defaultValue: "فرآیند راه‌اندازی" })}
                    highlightedText={t("sectionHeader.highlightedText", { ns: "InteractiveSetupProcess", defaultValue: "ساده" })}
                    descriptionHighlight={t("sectionHeader.descriptionHighlight", { ns: "InteractiveSetupProcess", defaultValue: "شروع کار" })}
                    descriptionText={t("sectionHeader.descriptionText", { ns: "InteractiveSetupProcess", defaultValue: "با دلبند تنها در چند مرحله" })}
                />
            </div>

            {/* Category Selector */}
            <div className="relative z-10 mx-auto mb-8">
                <div className="flex flex-col items-center justify-center mb-8">
                    <div className="bg-white/80 backdrop-blur-xl p-2 rounded-2xl border border-white/70 shadow-xl mb-8 w-full max-w-md hover:shadow-[0_10px_40px_rgba(0,114,206,0.15)] transition-all duration-500 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-[#EBF5FF] via-white to-[#EBF5FF] opacity-80"></div>
                        <motion.div
                            className="absolute -top-10 -right-10 w-20 h-20 bg-[#EBF5FF] rounded-full opacity-50"
                            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
                            transition={{ duration: 4, repeat: Infinity, repeatType: "reverse" }}
                        ></motion.div>
                        <motion.div
                            className="absolute -bottom-10 -left-10 w-20 h-20 bg-[#EBF5FF] rounded-full opacity-50"
                            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
                            transition={{ duration: 4, repeat: Infinity, repeatType: "reverse", delay: 2 }}
                        ></motion.div>

                        <div className={`flex flex-col sm:flex-row gap-3 relative z-10 p-1`}>
                            {processCategories.map((category) => {
                                const isActive = activeCategory === category.id;

                                return (
                                    <motion.button
                                        key={category.id}
                                        className={`flex items-center justify-start px-6 py-4 rounded-xl gap-3 w-full sm:flex-1
                                            transition-colors duration-500 ease-in-out
                                            ${isActive ? "bg-gradient-to-l from-[#0038A8] to-[#0072CE] text-white shadow-lg" : "bg-white/70 text-gray-700 hover:bg-white/90 hover:shadow-md"}`}
                                        onClick={() => {
                                            handleCategoryChange(category.id);
                                            if (window.navigator.vibrate) window.navigator.vibrate(50);
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, type: "spring", stiffness: 300, damping: 20 }}
                                    >
                                        <div className={`p-2 rounded-lg ${isActive ? "text-white bg-white/20" : "text-[#0072CE] bg-[#EBF5FF]"}`}>
                                            {category.id === "add-device" ? <Plus className="w-6 h-6" /> : <Radio className="w-6 h-6" />}
                                        </div>
                                        <div className={`flex flex-col  text-start`}>
                                            <span className="text-base font-bold">{category.title}</span>
                                            <span className={`text-xs ${isActive ? "text-white/80" : "text-gray-500"}`}>{category.subtitle}</span>
                                        </div>
                                    </motion.button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Description Box */}
                    <motion.div
                        className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-white/70 shadow-xl w-full relative overflow-hidden"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, type: "spring", stiffness: 100, damping: 15 }}
                        key={activeCategory}
                        whileHover={{ boxShadow: "0 20px 60px rgba(0, 114, 206, 0.15)" }}
                    >
                        <div className="absolute inset-0 opacity-5">
                            <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(#0072CE 1px, transparent 1px)", backgroundSize: "20px 20px" }}></div>
                        </div>

                        <motion.div initial={{ opacity: 0, x: activeCategory === "add-device" ? -20 : 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="relative z-10">
                            <div className={`text-start`}>
                                <div className={`flex items-center justify-start gap-3 mb-4`}>
                                    <div className="bg-[#EBF5FF] p-2 rounded-lg text-[#0072CE]">{activeCategory === "add-device" ? <Plus className="w-6 h-6" /> : <Radio className="w-6 h-6" />}</div>

                                    <h3 className="text-xl font-bold text-[#0072CE]">
                                        {t(`categories.${activeCategory === "add-device" ? "addDevice" : "addMonitoring"}.description.title`, { ns: "InteractiveSetupProcess", defaultValue: "" })}
                                    </h3>
                                </div>
                                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                                    {t(`categories.${activeCategory === "add-device" ? "addDevice" : "addMonitoring"}.description.text`, { ns: "InteractiveSetupProcess", defaultValue: "" })}
                                </p>
                                <div className="bg-[#EBF5FF]/50 p-4 rounded-lg border border-[#0072CE]/20 text-[#0072CE]/80 text-sm">
                                    <div className={`flex flex-row items-center gap-2 mb-2`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="font-bold">{t("labels.noteTitle", { ns: "InteractiveSetupProcess", defaultValue: "نکته مهم" })}</span>
                                    </div>
                                    {t(`categories.${activeCategory === "add-device" ? "addDevice" : "addMonitoring"}.description.note`, { ns: "InteractiveSetupProcess", defaultValue: "" })}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            {/* Process Steps - Desktop */}
            <div className="relative z-5 mx-auto">
                <div className="hidden sm:flex pb-14 pt-3 justify-center overflow-x-auto px-4 sm:px-0 hide-scrollbar">
                    <div className={`relative flex items-center flex-row`}>
                        {steps.map((step, index) => (
                            <React.Fragment key={index}>
                                <motion.button
                                    className={`relative z-10 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full 
                                        backdrop-blur-md ${
                                            activeStep > index
                                                ? "bg-gradient-to-br from-green-400/80 to-green-600/80 border-2 border-green-300"
                                                : activeStep === index
                                                ? "bg-gradient-to-br from-[#0038A8]/80 to-[#0072CE]/80 border-2 border-[#EBF5FF]"
                                                : "bg-white/30 border border-white/50"
                                        }
                                        transition-all duration-300`}
                                    onClick={() => handleStepClick(index)}
                                    onMouseEnter={() => setHoveredStep(index)}
                                    onMouseLeave={() => setHoveredStep(null)}
                                    whileHover={{ scale: 1.1, boxShadow: "0 0 25px rgba(0, 114, 206, 0.7)", transition: { duration: 0.3, type: "spring" } }}
                                    whileTap={{ scale: 0.95, boxShadow: "0 0 10px rgba(0, 114, 206, 0.8)", transition: { duration: 0.1 } }}
                                    animate={{
                                        scale: activeStep === index ? [1, 1.05, 1] : 1,
                                        boxShadow:
                                            activeStep === index
                                                ? ["0 0 15px rgba(0, 114, 206, 0.5)", "0 0 20px rgba(0, 114, 206, 0.7)", "0 0 15px rgba(0, 114, 206, 0.5)"]
                                                : "0 0 5px rgba(255, 255, 255, 0.3)",
                                    }}
                                    transition={{
                                        scale: { repeat: activeStep === index ? Infinity : 0, duration: 2 },
                                        boxShadow: { repeat: activeStep === index ? Infinity : 0, duration: 2 },
                                    }}
                                >
                                    <motion.div
                                        className={`absolute inset-0 rounded-full opacity-20 ${activeStep > index ? "bg-green-200" : activeStep === index ? "bg-[#EBF5FF]" : "bg-white/10"}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 0.2 }}
                                        layoutId={`step-bg-${index}`}
                                    />

                                    <motion.div
                                        className={`${activeStep > index ? "text-white" : activeStep === index ? "text-white" : "text-gray-600"}`}
                                        animate={{
                                            scale: activeStep === index ? [1, 1.2, 1] : 1,
                                            rotate: activeStep === index ? [0, 5, 0, -5, 0] : 0,
                                        }}
                                        transition={{ repeat: activeStep === index ? Infinity : 0, repeatDelay: 3, duration: 0.5 }}
                                    >
                                        {activeStep > index ? (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.5, rotate: -30 }}
                                                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                                transition={{ duration: 0.4, type: "spring" }}
                                                className="drop-shadow-md"
                                            >
                                                <Check className="w-6 h-6 sm:w-7 sm:h-7" />
                                            </motion.div>
                                        ) : (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.5, rotate: 30 }}
                                                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                                transition={{ duration: 0.4, type: "spring" }}
                                                className="drop-shadow-md"
                                            >
                                                {step.icon}
                                            </motion.div>
                                        )}
                                    </motion.div>

                                    <AnimatePresence>
                                        {hoveredStep === index && (
                                            <motion.div
                                                className="absolute -bottom-12 whitespace-nowrap text-sm rounded-xl px-4 py-2.5 backdrop-blur-xl bg-white/80 border border-white/70 shadow-xl font-medium z-20 text-gray-800"
                                                initial={{ opacity: 0, y: -10, scale: 0.9 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: -10, scale: 0.9 }}
                                                transition={{ duration: 0.25, type: "spring" }}
                                            >
                                                {step.title}
                                                <motion.div
                                                    className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 rotate-45 bg-white/80 border-t border-l border-white/70"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    <motion.div
                                        className={`absolute -top-2 ${
                                            isRTL ? "-right-2" : "-left-2"
                                        } w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-[#0038A8] to-[#0072CE] flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow-lg`}
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
                                    >
                                        {index + 1}
                                    </motion.div>
                                </motion.button>

                                {index < steps.length - 1 && (
                                    <div className="relative h-[3px] w-16 sm:w-24 mx-1">
                                        <motion.div className="absolute inset-0 rounded-full bg-gray-200/50 backdrop-blur-sm" />
                                        <motion.div
                                            className={`absolute inset-0 rounded-full bg-gradient-to-${isRTL ? "r" : "l"} from-[#0072CE] to-green-500`}
                                            initial={{ scaleX: 0, opacity: 0 }}
                                            animate={{ scaleX: activeStep > index ? 1 : 0, opacity: activeStep > index ? 1 : 0.3 }}
                                            transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
                                        />

                                        {activeStep > index && (
                                            <motion.div
                                                className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white shadow-md"
                                                animate={{ x: ["0%", "100%"], opacity: [0, 1, 0] }}
                                                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                            />
                                        )}
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Mobile View */}
                <div className="md:hidden mt-2 mb-6">
                    <div className="flex flex-col items-center gap-3 px-4">
                        {steps.map((step, index) => (
                            <motion.button
                                key={index}
                                className={`relative flex flex-row items-center w-full p-3.5 rounded-xl 
                                    backdrop-blur-md border-2 transition-all duration-300
                                    ${
                                        activeStep === index
                                            ? "bg-gradient-to-r from-[#0038A8]/20 to-[#0072CE]/10 border-[#0072CE]/50 shadow-[0_0_15px_rgba(0,114,206,0.3)]"
                                            : activeStep > index
                                            ? "bg-gradient-to-r from-green-500/20 to-green-600/10 border-green-400/50"
                                            : "bg-white/20 border-white/40"
                                    }`}
                                onClick={() => handleStepClick(index)}
                                whileHover={{ scale: 1.02, boxShadow: "0 0 15px rgba(0, 114, 206, 0.4)" }}
                                whileTap={{ scale: 0.98 }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                            >
                                {activeStep === index && (
                                    <motion.div
                                        className={`absolute ${isRTL ? "right-0" : "left-0"} top-0 bottom-0 w-1 bg-[#0072CE] ${isRTL ? "rounded-l-md" : "rounded-r-md"}`}
                                        initial={{ scaleY: 0 }}
                                        animate={{ scaleY: 1 }}
                                        transition={{ duration: 0.3 }}
                                        style={{ height: "70%", margin: "auto" }}
                                    />
                                )}

                                {activeStep > index && (
                                    <motion.div
                                        className={`absolute ${isRTL ? "right-0" : "left-0"} top-0 bottom-0 w-1 bg-green-500 ${isRTL ? "rounded-l-md" : "rounded-r-md"}`}
                                        initial={{ scaleY: 0 }}
                                        animate={{ scaleY: 1 }}
                                        transition={{ duration: 0.3 }}
                                        style={{ height: "70%", margin: "auto" }}
                                    />
                                )}

                                <div
                                    className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${isRTL ? "mr-3 ml-2" : "ml-3 mr-2"}
                                    ${
                                        activeStep > index
                                            ? "bg-gradient-to-br from-green-400/90 to-green-600/90 text-white"
                                            : activeStep === index
                                            ? "bg-gradient-to-br from-[#0038A8]/90 to-[#0072CE]/90 text-white"
                                            : "bg-white/30 text-gray-600"
                                    }
                                    relative`}
                                >
                                    {activeStep > index ? <Check className="w-6 h-6" /> : <div className="w-6 h-6">{step.icon}</div>}

                                    <div
                                        className={`absolute -top-1 ${
                                            isRTL ? "-right-1" : "-left-1"
                                        } w-5 h-5 rounded-full bg-gradient-to-br from-[#0038A8] to-[#0072CE] flex items-center justify-center text-white text-xs font-bold border border-white/70`}
                                    >
                                        {index + 1}
                                    </div>
                                </div>

                                <div className={`flex flex-col items-start text-${isRTL ? "right" : "left"} ${isRTL ? "ml-3" : "mr-3"} flex-grow`}>
                                    <span className={`text-sm font-bold ${activeStep > index ? "text-green-600" : activeStep === index ? "text-[#0072CE]" : "text-gray-700"}`}>{step.title}</span>
                                    <span className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                                        {step.description && step.description.length > 60 ? `${step.description.substring(0, 60)}...` : step.description}
                                    </span>
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </div>

                <div className="relative">
                    <AnimatePresence mode="wait">
                        <motion.div key={`${activeCategory}-${activeStep}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
                            <div className={`bg-white/70 backdrop-blur-xl border-white/70 shadow-xl w-full relative overflow-hidden border p-8 rounded-2xl text-${isRTL ? "right" : "left"}`}>
                                <motion.div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle at top ${isRTL ? "right" : "left"}, #0072CE, transparent 60%)` }} />

                                <div className="relative z-10">
                                    {/* Step Header */}
                                    <div className={`flex items-center mb-3 flex-row`}>
                                        <div
                                            className={`flex-shrink-0 h-12 w-12 rounded-lg bg-gradient-to-br from-[#0038A8] to-[#0072CE] flex items-center justify-center ${
                                                isRTL ? "mr-0 ml-3" : "ml-0 mr-3"
                                            } relative overflow-hidden`}
                                        >
                                            <div className="absolute inset-0 bg-pattern-dots opacity-10 bg-[#EBF5FF]"></div>
                                            <div className="absolute -inset-1 bg-[#EBF5FF] blur-md"></div>

                                            <motion.div
                                                className="relative z-10 text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.2)]"
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.95 }}
                                                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                            >
                                                {steps[activeStep]?.icon}
                                            </motion.div>
                                        </div>

                                        <div>
                                            <h3 className="text-xl font-bold text-[#0072CE]">{steps[activeStep]?.title}</h3>
                                        </div>
                                    </div>

                                    {/* Main Content */}
                                    <div className={`mt-6 grid grid-cols-1 md:grid-cols-12 gap-6 flex-row`}>
                                        {/* Details Section */}
                                        <div className={`md:col-span-7 text-start`}>
                                            <div className={`p-4 rounded-lg bg-[#EBF5FF]/50 border border-[#0072CE]/20 text-[#0072CE]/80 text-sm`}>
                                                <p className={`text-gray-600 text-sm leading-relaxed mb-3`}>{steps[activeStep]?.description}</p>

                                                <div className="space-y-2 mb-3">
                                                    {steps[activeStep]?.details?.map((detail, i) => (
                                                        <motion.div
                                                            key={i}
                                                            className={`flex items-start gap-2 text-start flex-row`}
                                                            initial={{ opacity: 0, x: -5 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ duration: 0.2, delay: i * 0.05 }}
                                                        >
                                                            <div className={`mt-1 h-3 w-3 flex-shrink-0 rounded-full bg-[#EBF5FF] flex items-center justify-center`}>
                                                                <div className={`h-1.5 w-1.5 rounded-full bg-[#0072CE]`}></div>
                                                            </div>
                                                            <p className={`text-gray-600 text-xs leading-relaxed flex-grow`}>{detail}</p>
                                                        </motion.div>
                                                    ))}
                                                </div>

                                                {/* Quick Guide */}
                                                <div className="pt-2 border-t border-dashed border-gray-300/30">
                                                    <h5 className={`text-xs font-bold mb-2 text-[#0072CE] flex items-center justify-start gap-1`}>
                                                        {t("labels.quickGuide", { ns: "InteractiveSetupProcess", defaultValue: "راهنمای سریع" })}
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </h5>
                                                    <div className={`flex flex-wrap gap-1 justify-start`}>
                                                        {steps[activeStep]?.quickGuide?.map((guide, idx) => (
                                                            <div key={idx} className={`px-2 py-1 rounded-full text-xs bg-[#EBF5FF] text-[#0072CE] border border-[#0072CE]/30`}>
                                                                {guide}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Image Section */}
                                        <div className="md:col-span-5 h-full">
                                            <div
                                                className={`sticky top-4 w-full h-full border-gray-300 rounded-lg bg-gradient-to-bl from-[#EBF5FF] to-[#F0F8FF] overflow-hidden flex items-center justify-center border border-[#0072CE]/30`}
                                            >
                                                <motion.div
                                                    className={`text-6xl text-[#0072CE] opacity-20 absolute`}
                                                    animate={{ scale: [1, 1.2, 1], rotate: [0, 5, 0, -5, 0] }}
                                                    transition={{ duration: 4, repeat: Infinity, repeatType: "reverse" }}
                                                >
                                                    {steps[activeStep]?.icon}
                                                </motion.div>

                                                <div className="relative z-10 w-full h-full flex items-center justify-center p-6">
                                                    <div className={`w-40 h-40 rounded-xl bg-[#EBF5FF] flex items-center justify-center border-2 border-[#0072CE]/30`}>
                                                        <motion.div className={`text-5xl text-[#0072CE]`} animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                                                            {steps[activeStep]?.icon}
                                                        </motion.div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Control Buttons */}
                                    <div className={`mt-6 flex flex-col justify-between sm:flex-row gap-3 sm:gap-0`}>
                                        <motion.button
                                            className={`px-5 py-2.5 rounded-lg flex items-center justify-center sm:justify-start gap-2 transition-all duration-300 ${
                                                activeStep === 0 ? "opacity-40 cursor-not-allowed" : "border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                            }`}
                                            whileHover={activeStep > 0 ? { scale: 1.02, x: isRTL ? -2 : 2 } : {}}
                                            whileTap={activeStep > 0 ? { scale: 0.98 } : {}}
                                            onClick={() => activeStep > 0 && setActiveStep(activeStep - 1)}
                                            disabled={activeStep === 0}
                                            aria-label={t("labels.previous", { ns: "InteractiveSetupProcess", defaultValue: "قبلی" })}
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"}></path>
                                            </svg>
                                            <span>{t("labels.previous", { ns: "InteractiveSetupProcess", defaultValue: "قبلی" })}</span>
                                        </motion.button>

                                        <div className={`hidden sm:flex items-center gap-1 flex-row order-first sm:order-none my-2 sm:my-0`}>
                                            {steps.map((_, idx) => (
                                                <motion.button key={idx} className="focus:outline-none" onClick={() => setActiveStep(idx)}>
                                                    <motion.div
                                                        className={`h-3 w-3 rounded-full transition-all duration-300 ${
                                                            idx === activeStep ? `bg-[#0072CE] scale-150` : idx < activeStep ? "bg-[#0072CE]/30" : "bg-gray-200/30"
                                                        }`}
                                                        animate={{ scale: idx === activeStep ? [1, 1.5, 1] : 1 }}
                                                        transition={{ repeat: idx === activeStep ? Infinity : 0, duration: 2, repeatType: "reverse" }}
                                                    />
                                                </motion.button>
                                            ))}
                                        </div>

                                        <motion.button
                                            className={`px-4 py-2.5 rounded-2xl flex items-center justify-center sm:justify-start gap-2 ${
                                                activeStep === steps.length - 1 ? "bg-green-500 text-white hover:bg-green-600" : `bg-gradient-to-l from-[#0038A8] to-[#0072CE] text-white`
                                            }`}
                                            whileHover={activeStep < steps.length - 1 ? { scale: 1.02, x: isRTL ? 2 : -2 } : { scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => {
                                                if (activeStep < steps.length - 1) {
                                                    setActiveStep(activeStep + 1);
                                                } else {
                                                    handleCompletion();
                                                }
                                            }}
                                            aria-label={
                                                activeStep === steps.length - 1
                                                    ? t("labels.complete", { ns: "InteractiveSetupProcess", defaultValue: "تکمیل" })
                                                    : t("labels.next", { ns: "InteractiveSetupProcess", defaultValue: "بعدی" })
                                            }
                                        >
                                            <span>
                                                {activeStep === steps.length - 1
                                                    ? t("labels.complete", { ns: "InteractiveSetupProcess", defaultValue: "تکمیل" })
                                                    : t("labels.next", { ns: "InteractiveSetupProcess", defaultValue: "بعدی" })}
                                            </span>
                                            {activeStep === steps.length - 1 ? (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                                </svg>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}></path>
                                                </svg>
                                            )}
                                        </motion.button>
                                    </div>
                                </div>

                                {/* Particle Effects */}
                                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                                    {[...Array(8)].map((_, i) => (
                                        <motion.div
                                            key={i}
                                            className={`absolute h-1 w-1 rounded-full ${colors.bg}`}
                                            style={{
                                                top: `${Math.random() * 100}%`,
                                                [isRTL ? "right" : "left"]: `${Math.random() * 100}%`,
                                                opacity: 0.4,
                                                boxShadow: `0 0 5px #00E5FF`,
                                            }}
                                            animate={{ y: [0, -30, 0], opacity: [0, 0.6, 0], scale: [0, 1, 0] }}
                                            transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: i * 0.7, ease: "easeInOut" }}
                                        />
                                    ))}

                                    <AnimatePresence>
                                        {steps[activeStep] &&
                                            [...Array(3)].map((_, i) => (
                                                <motion.div
                                                    key={`highlight-${activeStep}-${i}`}
                                                    className={`absolute h-2 w-2 rounded-full ${colors.bg}`}
                                                    style={{
                                                        top: `${50 + (Math.random() * 20 - 10)}%`,
                                                        [isRTL ? "right" : "left"]: `${50 + (Math.random() * 20 - 10)}%`,
                                                        boxShadow: `0 0 10px #0072CE`,
                                                    }}
                                                    initial={{ scale: 0, opacity: 0 }}
                                                    animate={{ scale: [0, 1.5, 0], opacity: [0, 0.8, 0] }}
                                                    exit={{ scale: 0, opacity: 0 }}
                                                    transition={{ duration: 2, delay: i * 0.2 }}
                                                />
                                            ))}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {/* Footer */}
            <div className={`flex flex-col-reverse sm:flex-row-reverse items-center justify-center gap-2 mt-4 mb-2`}>
                <a href="#" className={`flex items-center gap-2 text-gray-400 hover:text-[#0072CE] transition-colors duration-300 group px-3 py-1.5 text-xs sm:text-sm opacity-60 hover:opacity-80`}>
                    <span className="border-b border-transparent group-hover:border-current">
                        {t("labels.videoTutorial", { ns: "InteractiveSetupProcess", defaultValue: "آموزش ویدیویی راه‌اندازی" })}
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z"
                        />
                    </svg>
                </a>
                <p className="text-xs text-gray-500 py-1.5 opacity-50">{t("labels.needHelp", { ns: "InteractiveSetupProcess", defaultValue: "نیاز به راهنمایی بیشتر دارید؟" })}</p>
            </div>
        </div>
    );
};

export default InteractiveSetupProcess;